import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import { Ruler, MapPin, Landmark, Network, Search, Lightbulb } from "lucide-react";
import { PALETTE, BG_PALETTE } from "../lib/theme";
import { cn } from "@ui";

export const TAB_ICONS: LucideIcon[] = [Ruler, MapPin, Landmark, Network, Search, Lightbulb];

export interface SectionTabItem {
  titre: string;
  colorIndex: number;
  Icon: LucideIcon;
}

interface Props {
  sections: SectionTabItem[];
  active: number;
  onChange: (index: number) => void;
}

export function SectionTabs({ sections, active, onChange }: Props) {
  // Five pills in a horizontally-scrolling row will overflow on phones —
  // when the active pill is off-screen (e.g. user swiped to "Idées" but
  // the row is still showing tabs 0-2), the user loses the context cue.
  // We track each pill via a ref array and scroll the active one into
  // the visible center every time `active` changes.
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const el = tabRefs.current[active];
    if (!el) return;
    el.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [active]);

  return (
    <nav
      aria-label="Sections de l'analyse"
      className="mb-5 flex gap-2 overflow-x-auto pb-1 scrollbar-none"
      style={{ scrollbarWidth: "none" }}
    >
      {sections.map((sec, i) => {
        const { Icon, colorIndex, titre } = sec;
        const isActive = active === i;
        return (
          <button
            key={`tab-${i}`}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            onClick={() => onChange(i)}
            aria-pressed={isActive}
            className={cn(
              // Mobile: 40px min-height keeps these tappable per Apple HIG.
              "shrink-0 whitespace-nowrap flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm min-h-[40px]",
              "transition-colors border",
              isActive ? "font-bold" : "font-normal text-slate-400 border-slate-200",
            )}
            style={
              isActive
                ? {
                    background: BG_PALETTE[colorIndex],
                    color: PALETTE[colorIndex],
                    borderColor: PALETTE[colorIndex],
                  }
                : { background: "transparent" }
            }
          >
            <Icon
              size={16}
              color={isActive ? PALETTE[colorIndex] : "#bbb"}
              strokeWidth={2}
            />
            {titre}
          </button>
        );
      })}
    </nav>
  );
}
